/**
 * Storm-games auth — same Supabase project + same `storm-auth` storageKey
 * as the portal. If the player is already logged in on games.aiwari.com,
 * AIWARI picks up that session automatically (cookie/localStorage are
 * shared across the same origin).
 *
 * Magic-link sign-in is the only flow we expose right now (no passwords,
 * no SSO config). The login panel emails a one-tap link; on return the
 * URL hash carries access/refresh tokens that supabase-js auto-detects.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { create } from 'zustand';

const SUPABASE_URL = (import.meta.env.VITE_AIWARI_SUPABASE_URL as string | undefined)
  ?? 'https://lamlhtrbvoaeqlgfksap.supabase.co';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_AIWARI_SUPABASE_ANON_KEY as string | undefined)
  ?? 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxhbWxodHJidm9hZXFsZ2Zrc2FwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMTQ4NzQsImV4cCI6MjA5MjY5MDg3NH0.12q9ii5jUJoCafl3bGDS24d1SyOWqLv1MRwNWdPJQsM';

let _client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!_client) {
    _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'implicit',
        // Same key as @storm/core — shares session with the portal and
        // every other game on games.aiwari.com.
        storageKey: 'storm-auth',
      },
      global: {
        headers: { 'x-storm-game': 'aiwari' },
      },
    });
  }
  return _client;
}

interface AuthStore {
  userId: string | null;
  email: string | null;
  loading: boolean;
  setSession: (userId: string | null, email: string | null) => void;
  setLoading: (b: boolean) => void;
}

export const useAuth = create<AuthStore>((set) => ({
  userId: null,
  email: null,
  loading: true,
  setSession: (userId, email) => set({ userId, email, loading: false }),
  setLoading: (b) => set({ loading: b }),
}));

/** Boot the auth listener — call once at app start. */
export function initAuth(): () => void {
  const supabase = getSupabase();

  // Hydrate from current session.
  supabase.auth.getSession().then(({ data }) => {
    const u = data.session?.user;
    useAuth.getState().setSession(u?.id ?? null, u?.email ?? null);
    if (u?.id) {
      try { localStorage.setItem('aiwari-storm-id', u.id); } catch {}
    }
  });

  const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
    const u = session?.user;
    useAuth.getState().setSession(u?.id ?? null, u?.email ?? null);
    if (u?.id) {
      try { localStorage.setItem('aiwari-storm-id', u.id); } catch {}
    } else {
      try { localStorage.removeItem('aiwari-storm-id'); } catch {}
    }
  });

  return () => sub.subscription.unsubscribe();
}

export async function signInWithEmail(email: string): Promise<{ ok: boolean; error?: string }> {
  const supabase = getSupabase();
  const redirectTo = `${location.origin}${location.pathname}`;
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo, shouldCreateUser: true },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** R232q71: Google OAuth — Supabase provider пред-настроен (см. session 2026-04-28). */
export async function signInWithGoogle(): Promise<{ ok: boolean; error?: string }> {
  const supabase = getSupabase();
  const redirectTo = `${location.origin}${location.pathname}`;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      queryParams: { access_type: 'offline', prompt: 'consent' },
    },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** R232q71: email + пароль для тех, кто не хочет ждать magic-link. */
export async function signUpWithPassword(email: string, password: string): Promise<{ ok: boolean; needsVerification?: boolean; error?: string }> {
  const supabase = getSupabase();
  const redirectTo = `${location.origin}${location.pathname}`;
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: redirectTo },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, needsVerification: !data.session };
}

export async function signInWithPassword(email: string, password: string): Promise<{ ok: boolean; error?: string }> {
  const supabase = getSupabase();
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function signOut(): Promise<void> {
  const supabase = getSupabase();
  await supabase.auth.signOut();
  try { localStorage.removeItem('aiwari-storm-id'); } catch {}
}
